<template>
  <div :class="['tenancy-overview', { 'free-tenancy': isFree }]">
    <SubscriptionModal
      :show="shouldShowModal"
      :currentPlanName="subscriptionPlanName"
      :plans="getPlans"
      :shouldShowCPIAdjustmentDetails="shouldShowCPIAdjustmentDetails"
      :loading="isUpdatingSubscription"
      @input="handleSubscriptionChange"
      @close="shouldShowSubscriptionModal = shouldShowSubscriptionModalIndexReg = false"
    />

    <h2>{{ defaultHeader }}</h2>

    <div class="d-flex justify-content-end">
      <hl-Tag v-if="getTenancyStatus === 'active'" variation="primary"> Aktivt leieforhold </hl-Tag>
      <hl-Tag v-else-if="getTenancyStatus === 'in-cancellation'" variation="warning">
        Leieforholdet er i avslutningsfasen
      </hl-Tag>
      <hl-Tag v-else-if="getTenancyStatus === 'cancelled'" variation="danger"> Avsluttet leieforhold </hl-Tag>
    </div>

    <hl-Toast
      v-if="getTenancy.subscription.subscriptionPlanName === 'Garantert'"
      :title="translate('tenancy.ddg.warning.title')"
      :text="translate('tenancy.ddg.warning.md-text')"
      variant="warning"
      class="mt-3"
    />

    <div v-if="getMainTenant" class="grid-container">
      <div class="invoiceStatus">
        <InvoiceStatus
          :landlordId="landlordId"
          :tenancyId="tenancyId"
          :is-disabled="isFree"
          :accounting-summary="accountingSummary"
        />
      </div>

      <div class="contract">
        <router-link :to="{ name: 'tenancy_tenancy' }" class="no-link-styles">
          <BlockContainer hasHover class="p-4">
            <div class="d-flex align-items-center mb-4">
              <i class="h4 budicon-user" />
              <h5 class="ml-4">Kontraktsdetaljer</h5>
            </div>

            <div class="row mt-5">
              <div class="col">
                <p class="m-0">Leietaker(e)</p>
                <h6>
                  {{ getTenancy.tenants[0].fullName }}
                  <span v-if="getTenancy.tenants.length > 1"> (+{{ getTenancy.tenants.length - 1 }}) </span>
                </h6>
              </div>
              <div v-if="!hasPendingChangeOrCancel" class="col">
                <p class="m-0">Abonnement</p>
                <h6>{{ getTenancy.subscription.subscriptionPlanName }}</h6>
              </div>
              <div v-else class="col"></div>
            </div>

            <div v-if="hasPendingChangeOrCancel" class="row mt-5">
              <div class="col">
                <p class="m-0">Abonnement</p>
                <h6>{{ getTenancy.subscription.subscriptionPlanName }}</h6>
              </div>
              <div v-if="getTenancy?.subscription?.pendingCancelation" class="col">
                <p class="m-0">Kansellering</p>
                <h6>Dato: <hl-Date :date="getTenancy?.subscription?.pendingCancelation?.endDate" /></h6>
                <h6>Begrunnelse: {{ getCancellationReasonText(getTenancy?.subscription?.pendingCancelation?.cancellationReason) }}</h6>
              </div>
              <div v-else-if="getTenancy?.subscription?.pendingChange" class="col">
                <p class="m-0">Endring</p>
                <h6>Dato: <hl-Date :date="getTenancy?.subscription?.pendingChange?.changeDate" /></h6>
                <h6>Til: {{ getPlanNameById(getTenancy?.subscription?.pendingChange?.newSubscriptionPlanId) }}</h6>
              </div>
            </div>


            <div class="row mt-4">
              <div class="col">
                <p class="m-0">Telefon</p>
                <h6>{{ mainTenant.cellPhone }}</h6>
              </div>
              <div class="col">
                <p class="m-0">E-post</p>
                <h6>{{ mainTenant.email }}</h6>
              </div>
            </div>

            <div class="row mt-4">
              <div class="col">
                <p class="m-0">Startdato</p>
                <h6>
                  <hl-Date :date="getTenancy.startDate" />
                </h6>
              </div>
              <div class="col">
                <p class="m-0">Sluttdato</p>
                <h6>
                  <template v-if="!getTenancy.endDate"> Ingen sluttdato </template>
                  <hl-Date v-else :date="getTenancy.endDate" />
                </h6>
              </div>
            </div>

            <div class="row mt-4">
              <div class="col">
                <p class="m-0">Forfallsdag</p>
                <h6>{{ getTenancy.rent.dueDay }}</h6>
              </div>
              <div class="col">
                <p class="m-0">Husleie /mnd</p>
                <h6>{{ getTenancy.rent.rent }}</h6>
              </div>
            </div>

            <div class="row mt-3">
              <div class="col">
                <p class="m-0">Leieobjekt</p>
                <router-link v-if="rentalObject" :to="{ name: 'rentalObject_overview', params: { rentalObjectId } }">
                  <h6>
                    {{ rentalObject.address.street }}
                  </h6>
                </router-link>
              </div>
            </div>
          </BlockContainer>
        </router-link>
      </div>

      <div v-if="getTenancy.subscription.subscriptionPlanName !== 'Garantert'" class="CPIAdjustment">
        <CPIAdjustment
          :landlordId="landlordId"
          :tenancyId="tenancyId"
          :is-disabled="false"
          :rent-adjustment="rentAdjustment"
          is-full-height
          @click="handleCPIAdjustment"
        />
      </div>

      <div class="deposit">
        <router-link :to="{ name: 'tenancy_deposit' }" class="no-link-styles">
          <BlockContainer
            hasHover
            class="p-4 align-items-center border-deposit-status"
            :class="getTenancyDepositStatus"
          >
            <div>
              <div>
                <div class="d-flex align-items-center mb-4">
                  <i class="h4 budicon-savings" />
                  <h5 class="ml-4">Depositum</h5>
                </div>
                <div>
                  <div v-if="getPriorityTenancyDeposit" class="mb-2">
                    <div class="deposit-category mb-1">
                      {{ getPriorityTenancyDepositCategory }}
                    </div>

                    <p class="mt-3">Beløp: {{ getPriorityTenancyDeposit.amount | toCurrency }}</p>
                  </div>
                  <div v-else>
                    <p>Klikk her for å opprette depositum for leieforholdet.</p>
                  </div>
                </div>
              </div>
            </div>
          </BlockContainer>
        </router-link>
      </div>
      <div class="notes">
        <router-link :to="{ name: 'tenancy_documents' }" class="no-link-styles">
          <BlockContainer hasHover class="p-4">
            <div class="d-flex align-items-center mb-4">
              <i class="h4 budicon-note-information" />
              <h5 class="ml-4">Notater</h5>
            </div>
            <p>Antall notater: {{ notes.length }}</p>
          </BlockContainer>
        </router-link>
      </div>
      <div class="documents">
        <router-link :to="{ name: 'tenancy_documents' }" class="no-link-styles">
          <BlockContainer hasHover class="p-4">
            <div class="d-flex align-items-center mb-4">
              <i class="h4 budicon-document-page" />
              <h5 class="ml-4">Dokumenter</h5>
            </div>

            <hl-LoaderDots v-if="isLoadingDocuments" />
            <p v-else>Antall dokumenter: {{ documents.length }}</p>
          </BlockContainer>
        </router-link>
      </div>
      <div class="upsell">
        <SubscriptionUpsellCard
          v-if="shouldShowUpsell"
          v-bind="upsellCardProps"
          @input="handleSubscriptionUpsellCard"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex';
import BlockContainer from '@/components/BlockContainer';
import { getDepositCategory, getDepositStatus } from '@/lib/depositMaps';
import subscription from '@/mixins/tenancy/subscription';
import Currency from '@modules/husleie-framework/src/components/currency/Currency.vue';
import Tag from '@modules/husleie-framework/src/components/forms/inputs/tags/Tag.vue';
import Date from '@modules/husleie-framework/src/components/time/Date.vue';
import InvoiceStatus from '@/components/tenancy/overview/cards/invoiceStatus';
import CPIAdjustment from '@/components/tenancy/overview/cards/CPIAdjustment';
import i18nCommon from '@modules/i18n/mixins/Common';
import { TenancyApi } from '@modules/husleieapi2';
import { LandlordApi } from '@modules/husleieapi2';
import SubscriptionUpsellCard from '@/components/subscription/SubscriptionUpsellCard.vue';
import SubscriptionModal from '@/components/detailedRentalObject/SubscriptionModal.vue';
import Toast from '@modules/husleie-framework/src/components/Toast.vue';
import LoaderDots from '@modules/husleie-framework/src/components/loaders/LoaderDots.vue';

export default {
  name: 'TenancyOverview',
  mixins: [i18nCommon, subscription],
  components: {
    BlockContainer,
    InvoiceStatus,
    CPIAdjustment,
    'hl-Currency': Currency,
    'hl-Date': Date,
    'hl-Tag': Tag,
    'hl-Toast': Toast,
    'hl-LoaderDots': LoaderDots,
    SubscriptionUpsellCard,
    SubscriptionModal,
  },

  props: {
    tenancyId: [String, Number],
    landlordId: [String, Number],
    defaultHeader: String,
  },

  data() {
    return {
      availableSubscriptions: [],
      downgrading: false,
      plans: [],
      planTerms: false,
      accountingSummary: null,
      tenancyStatusMap: {
        free: {
          value: 'Gratis',
          variation: 'primary',
        },
        active: {
          value: 'Aktivt',
          variation: 'primary',
        },
        'in-cancellation': {
          value: 'I avslutning',
          variation: 'warning',
        },
        cancelled: {
          value: 'Avsluttet',
          variation: 'danger',
        },
      },
      upgrading: false,
      notes: [],
      documents: [],
      shouldShowSubscriptionModal: false,
      currentSelectedPlanName: '',
      plans: [],
      shouldShowSubscriptionModalIndexReg: false,
      isUpdatingSubscription: false,
      isLoadingDocuments: false,
    };
  },

  watch: {
    tenancyId: {
      handler: 'fetchDeposits',
      immediate: true,
    },
  },

  computed: {
    ...mapGetters(['getRentalObject', 'getLandlordProfileMeta']),
    ...mapGetters('tenancy', [
      'getTenancyOrders',
      'getTenancyStatus',
      'getTenancyTenants',
      'getTenancy',
      'getMainTenant',
      'getCoTenants',
      'getTenancyDeposits',
    ]),

    getPriorityTenancyDeposit() {
      const deposits = this.getTenancyDeposits.sort(deposit => {
        return deposit.status === 'active' ? -1 : 0;
      });

      const deposit = deposits.find(deposit => deposit.category === 'easybank_deposit_account');
      if (deposit) {
        return deposit;
      }
      return deposits.length > 0 ? deposits[0] : undefined;
    },
    getTenancyDepositStatus() {
      const deposit = this.getPriorityTenancyDeposit;
      if (!deposit) {
        return '';
      }
      return (
        {
          active: 'border-status-primary',
          initial: 'border-status-danger',
          ended: 'border-status-danger',
          conflict: 'border-status-warning',
        }[deposit.status] || ''
      );
    },

    getPriorityTenancyDepositCategory() {
      return getDepositCategory(this.getPriorityTenancyDeposit.category);
    },

    getPriorityTenancyDepositStatus() {
      return getDepositStatus({
        status: this.getPriorityTenancyDeposit.status,
        category: this.getPriorityTenancyDeposit.category,
        funds: this.getPriorityTenancyDeposit.meta.funds,
        amount: this.getPriorityTenancyDeposit.amount,
      });
    },
    getPriorityTenancyDepositStatusName() {
      return this.getPriorityTenancyDepositStatus.name;
    },
    getPriorityTenancyDepositStatusVariation() {
      return this.getPriorityTenancyDepositStatus.variation;
    },

    getOtherTenancyDepositCount() {
      return this.getTenancyDeposits ? this.getTenancyDeposits.length - 1 : 0;
    },

    debtCollectionMethod() {
      return this.getTenancy.settings.debtCollection;
    },

    rentAdjustment() {
      return this.getTenancy.rentAdjustment;
    },

    rentalObjectId() {
      return this.$route.params.rentalObjectId;
    },

    rentalObject() {
      return this.getRentalObject(this.rentalObjectId);
    },

    tenancyBaseRent() {
      const rent = this.getTenancy.rent.rent;
      if (rent == null) return;
      return rent;
    },

    mainTenant() {
      return this.getTenancy.tenants[0];
    },

    subscriptionPlanName() {
      return this.getTenancy.subscription.subscriptionPlanName || '';
    },

    shouldShowUpsell() {
      if (!this.subscriptionPlanName || this.subscriptionPlanName === 'Basic') {
        return true;
      }
      return false;
    },

    noneChosenUpsellCardProps() {
      return {
        header: this.translate('tenancyOverview.upsell.basic.header'),
        text: this.translate('tenancyOverview.upsell.basic.text'),
        subscriptionPlanName: this.subscriptionPlanName,
        translationButton1: this.translate('tenancyOverview.upsell.basic.button1'),
        translationButton2: this.translate('tenancyOverview.upsell.basic.button2'),
        benefits: [
          'Fakturering av husleien',
          'Indeksregulering',
          'Betalingsoppfølging',
          'Forsikring mot ubet. leie',
          'Bistand til utkastelse',
          'Boligskadeforsikring',
          'Depositumskonto',
          'Merutgifter utkastelse',
          'Telefonsupport',
          'Advokat',
        ],
      };
    },

    premiumUpsellCardProps() {
      return {
        header: this.translate('tenancyOverview.upsell.premium.header'),
        text: this.translate('tenancyOverview.upsell.premium.text'),
        subscriptionPlanName: this.subscriptionPlanName,
        translationButton1: this.translate('tenancyOverview.upsell.premium.button1'),
        translationButton2: this.translate('tenancyOverview.upsell.premium.button2'),
        benefits: [
          'Indeksregulering',
          'Merutgifter utkastelse',
          'Forsikring mot ubet. leie',
          'Utvidet tid med advokat',
          'Boligskadeforsikring',
        ],
      };
    },

    upsellCardProps() {
      if (!this.subscriptionPlanName) {
        return this.noneChosenUpsellCardProps;
      }
      if (this.subscriptionPlanName === 'Basic') {
        return this.premiumUpsellCardProps;
      }
      return undefined;
    },

    shouldShowModal() {
      return [this.shouldShowSubscriptionModal, this.shouldShowSubscriptionModalIndexReg].includes(true);
    },

    getPlans() {
      if (!this.shouldShowSubscriptionModalIndexReg) {
        return this.plans;
      }
      return [this.plans.find(plan => plan.name === 'Premium')].filter(plan => plan);
    },

    shouldShowCPIAdjustmentDetails() {
      return this.shouldShowSubscriptionModalIndexReg && ['', 'Basic'].includes(this.subscriptionPlanName);
    },

    isProfessionalLandlord() {
      const landlord = this.getLandlordProfileMeta(this.landlordId);

      return landlord && landlord.meta.isProfessional;
    },

    translationErrorCouldNotRetrieveSummary() {
      return this.translate('error.could not retrieve summary');
    },
    translationErrorCouldNotRetrieveDeposit() {
      return this.translate('error.could not load deposit');
    },
    hasPendingChangeOrCancel() {
      return this.getTenancy?.subscription?.pendingChange || this.getTenancy?.subscription?.pendingCancelation
    }
  },

  methods: {
    ...mapMutations('tenancy', ['setTenancySubscriptionPlanName']),
    ...mapActions('tenancy', [
      'fetchTenancyAccountingSummary',
      'fetchTenancyDeposits',
      'fetchTenancyNotes',
      'fetchTenancy',
    ]),

    cardPrice(subscriptionName) {
      const subscription = this.availableSubscriptions.find(sub => sub.name === subscriptionName);
      return subscription?.price;
    },

    async fetchAccountingSummary() {
      try {
        this.accountingSummary = await this.fetchTenancyAccountingSummary({
          landlordId: this.landlordId,
          tenancyId: this.tenancyId,
        });
      } catch (error) {
        console.error(error);
        this.$toast(this.translationErrorCouldNotRetrieveSummary, 'error');
      }
    },

    async fetchDeposits(tenancyId) {
      try {
        await this.fetchTenancyDeposits({
          landlordId: this.landlordId,
          tenancyId,
        });
      } catch (error) {
        console.error(error);
        this.$toast(this.translationErrorCouldNotRetrieveDeposit, 'error');
      }
    },

    async loadAvailableSubscriptions() {
      const api = new LandlordApi();
      try {
        this.availableSubscriptions = await api.getLandlordAvailableSubscriptions(this.landlordId);
      } catch (error) {
        this.$toast(this.translationErrorCouldNotGetSubscriptions, 'error');
      }
    },

    async fetchDocumentationData() {
      this.isLoadingDocuments = true;

      this.notes = await this.fetchTenancyNotes({
        landlordId: this.landlordId,
        tenancyId: this.tenancyId,
      });
      const api = new TenancyApi();
      this.documents = await api.getLandlordTenancyDocuments(this.landlordId, this.tenancyId);

      this.isLoadingDocuments = false;
    },

    async fetchAvailablePlans() {
      try {
        const api = new TenancyApi();
        this.plans = await api.getLandlordTenancyAvailableSubscriptions(this.landlordId, this.tenancyId);
      } catch (error) {
        throw new Error('getLandlordTenancyAvailableSubscriptions: ', error);
      }
    },
    async updateTenancyStore() {
      try {
        this.fetchTenancy({
          landlordId: this.landlordId,
          tenancyId: this.tenancyId,
        });
      } catch (error) {
        throw new Error('Failed to fetch tenancy', error);
      }
    },
    async handleSubscriptionChange(newPlanName) {
      this.isUpdatingSubscription = true;

      try {
        await this.changeSubscriptionPlan(
          this.landlordId,
          this.tenancyId,
          this.plans,
          this.subscriptionPlanName,
          newPlanName,
          'tenancyOverview'
        );
        this.$toast(
          this.translate('subscription.alert.changed subscription to {planName}', { planName: newPlanName }),
          'success'
        );
        await this.updateTenancyStore();
      } catch {
        this.$toast(
          this.translate('subscription.alert.change of subscription to {planName} failed', { planName: newPlanName }),
          'error'
        );
      }
      this.isUpdatingSubscription = this.shouldShowSubscriptionModal = this.shouldShowSubscriptionModalIndexReg = false;
    },
    handleSubscriptionUpsellCard() {
      this.shouldShowSubscriptionModal = true;
    },
    handleCPIAdjustment() {
      if (!this.isProfessionalLandlord && ['', 'Basic'].includes(this.subscriptionPlanName)) {
        this.shouldShowSubscriptionModalIndexReg = true;
      } else {
        this.$router.push({
          name: 'tenancy_settings',
          query: {
            highlight: 'kpi',
          },
        });
      }
    },
    getPlanNameById(planId) {
      const plan = this.plans.find(plan => plan.id === planId);
      return plan ? plan.name : 'Basic'; // Basic as default since all pending changes is from premium to basic, others will be pending cancel (to free)
    },
  },

  async created() {
    this.fetchDocumentationData();
    this.fetchAccountingSummary();
    this.loadAvailableSubscriptions();
    this.fetchAvailablePlans();
  },
};
</script>

<style scoped lang="scss">
.bg-op-info {
  background: rgba($info, 0.2);
}

.bg-op-primary {
  background: rgba($primary, 0.2);
}

.bg-op-secondary {
  background: rgba($secondary, 0.2);
}

.bg-op-success {
  background: rgba($success, 0.2);
}

.grid-container {
  display: grid;
  grid-gap: 1rem;
  grid-template-columns: minmax(100%, 1fr);
  grid-template-areas:
    'banner'
    'invoiceStatus'
    'contract'
    'CPIAdjustment'
    'deposit'
    'notes'
    'documents'
    'upsell';
}

.free-tenancy .grid-container {
  grid-template-areas:
    'contract'
    'invoiceStatus'
    'CPIAdjustment'
    'deposit'
    'notes'
    'documents'
    'upsell';
}

.inpage-upsell-row {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.inpage-upsell-row-item {
  display: flex;
  flex-direction: row;
  gap: 0.25em;
  align-items: center;
}

.inpage-upsell-row i {
  font-size: 1.75rem;
  position: relative;
  top: 2px;
}

@media (min-width: 1000px) {
  .grid-container {
    grid-template-columns: repeat(2, 1fr);
    grid-template-areas:
      'banner banner'
      'invoiceStatus invoiceStatus'
      'contract contract'
      'CPIAdjustment deposit'
      'notes documents'
      'upsell upsell';
  }

  .free-tenancy .grid-container {
    grid-template-areas:
      'banner banner'
      'invoiceStatus invoiceStatus'
      'contract contract'
      'CPIAdjustment deposit'
      'notes documents'
      'upsell upsell';
  }
}

@media (min-width: 1280px) {
  .grid-container {
    grid-template-columns: repeat(3, 1fr);
    grid-template-areas:
      'banner banner banner'
      'contract contract invoiceStatus'
      'contract contract deposit'
      'notes documents CPIAdjustment'
      'upsell upsell upsell';
  }

  .free-tenancy .grid-container {
    grid-template-areas:
      'banner banner banner'
      'contract contract invoiceStatus'
      'contract contract deposit'
      'notes documents CPIAdjustment'
      'upsell upsell upsell';
  }

  .inpage-upsell-row {
    display: flex;
    flex-direction: row;
    gap: 1rem;
  }
}

.grid-container > div .blockContainer {
  height: 100%;
}

.invoiceStatus {
  grid-area: invoiceStatus;
}

.contract {
  grid-area: contract;
}

.CPIAdjustment {
  grid-area: CPIAdjustment;
}

.upsell {
  grid-area: upsell;
}

.deposit {
  grid-area: deposit;
}

.banner {
  grid-area: banner;
}

.notes {
  grid-area: notes;
}

.documents {
  grid-area: documents;
}

.no-link-styles {
  text-decoration: none;
  color: inherit;
}

.deposit-category {
  font-weight: bold;
}

.border-deposit-status {
  border-left-width: 8px;
  border-left-style: solid;
}

.border-status-warning {
  border-left-color: $warning;
}

.border-status-primary {
  border-left-color: $primary;
}

.border-status-danger {
  border-left-color: $danger;
}
</style>
